import React, { useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaPlay } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import Field, { NativeSelect, TextInput } from '../../../components/ui/Field';
import { engineService } from '../../../services/engineService';
import { isPromptExpr, previewParts } from '../../../utils/templatePreview';

// Lo que se puede poner en las Instrucciones entre {{ }}. El motor llena solo estas raíces (contacto, mensaje, ahora, datos, agente) y el
// código {{= … }}; cualquier otro {{…}} queda tal cual (los prompts que venían de n8n no se rompen).
export function promptVariables(preTools = []) {
  return [
    { group: 'De la conversación', items: [
      { text: '{{contacto.nombre}}', label: 'Nombre de la persona en el canal' },
      { text: '{{contacto.telefono}}', label: 'Su teléfono (WhatsApp)' },
      { text: '{{contacto.canal}}', label: 'Canal: whatsapp, telegram o web' },
      { text: '{{mensaje}}', label: 'Lo que acaba de escribir' },
      { text: '{{ahora | fecha:"larga"}}', label: 'Fecha de hoy (9 de octubre de 2026)' },
      { text: '{{ahora | fecha:"hora"}}', label: 'Fecha y hora (09/10/2026 12:30)' },
    ] },
    ...(preTools.length ? [{ group: 'Datos previos (Herramientas › Antes de responder)', items: preTools.map((p) => ({ text: `{{datos.${p.as}.texto}}`, label: `Resultado de «${p.as}»` })) }] : []),
    { group: 'Código de una línea', items: [
      { text: '{{= fechaMx().diaSemana }}', label: 'Día de la semana (lunes, martes…)' },
      { text: '{{= fechaMx().hora < 12 ? "Buenos días" : fechaMx().hora < 19 ? "Buenas tardes" : "Buenas noches" }}', label: 'Saludo según la hora' },
      { text: '{{= formatoFecha(sumarDias(ahora, 1), "larga") }}', label: 'La fecha de mañana' },
      { text: '{{= (contacto.nombre || "").split(" ")[0] }}', label: 'Solo el primer nombre' },
    ] },
  ];
}

export function VariablesMenu({ preTools, onInsert, disabled }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} disabled={disabled} onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[12.5px] text-fg-dim hover:text-fg rounded-md disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/60">
        Insertar dato <FaChevronDown aria-hidden className="h-2.5 w-2.5" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-2 w-[min(26rem,90vw)] max-h-[60vh] overflow-y-auto rounded-xl bg-panel ring-1 ring-line p-2" style={{ boxShadow: 'var(--shadow)' }}>
          {promptVariables(preTools).map((g) => (
            <div key={g.group} className="mb-1">
              <p className="px-2.5 pt-2 pb-1 text-[11.5px] font-semibold text-fg-faint">{g.group}</p>
              {g.items.map((it) => (
                <button key={it.text} type="button" role="menuitem" onClick={() => { onInsert(it.text); setOpen(false); }}
                  className="block w-full text-left rounded-lg px-2.5 py-1.5 hover:bg-raised focus-visible:outline-none focus-visible:bg-raised">
                  <span className="block text-[13px] text-fg">{it.label}</span>
                  <code className="block truncate text-[11.5px] text-live">{it.text}</code>
                </button>
              ))}
            </div>
          ))}
          <p className="px-2.5 py-2 text-[11.5px] leading-relaxed text-fg-faint">El código va en <code>{'{{= … }}'}</code>: JavaScript de una línea con <code>contacto</code>, <code>mensaje</code>, <code>ahora</code> y <code>datos</code>, y las ayudas fechaMx(), formatoFecha(fecha, "corta"|"larga"|"hora"), sumarDias(fecha, n) y pesos(n). Hora de México.</p>
        </div>
      )}
    </div>
  );
}

export const SAMPLE = { telefono: '5215512345678', nombre: 'Ana López', canal: 'whatsapp', mensaje: 'Hola, quiero información' };

/**
 * Contacto de prueba y última prueba de los datos previos, compartidos entre «Antes de responder» e Instrucciones (por agente, en
 * sessionStorage: se pierden al cerrar la pestaña). Así, después de probar las herramientas, las Instrucciones ya autocompletan y
 * muestran sus campos reales.
 */
const sessionKey = (agentId) => `agent.${agentId}.preview`;
const readSession = (agentId) => { try { return JSON.parse(sessionStorage.getItem(sessionKey(agentId)) || 'null'); } catch { return null; } };
const listeners = new Set();
export function usePreviewSession(agentId) {
  const [st, setSt] = useState(() => ({ sample: SAMPLE, run: null, ...(readSession(agentId) || {}) }));
  useEffect(() => {
    const on = (id, next) => { if (id === agentId) setSt(next); };
    listeners.add(on); return () => listeners.delete(on);
  }, [agentId]);
  const cur = useRef(st); cur.current = st;
  const update = (patch) => {
    const next = { ...cur.current, ...patch };
    cur.current = next;
    try { sessionStorage.setItem(sessionKey(agentId), JSON.stringify(next)); } catch { /* sin almacenamiento: solo en memoria */ }
    for (const l of listeners) l(agentId, next);   // incluye a este mismo componente
  };
  return { sample: st.sample, run: st.run, setSample: (sample) => update({ sample }), setRun: (run) => update({ run }) };
}

/** Datos con los que se arma la vista previa: el contacto de prueba y, si ya se probó, los datos previos reales. */
export function promptScope(agent, sample, run) {
  return {
    contacto: { nombre: sample.nombre, telefono: sample.telefono || null, canal: sample.canal },
    mensaje: sample.mensaje, ahora: new Date().toISOString(), agente: { id: agent.id },
    ...(run?.datos ? { datos: run.datos } : {}),
  };
}

/**
 * Vista previa EN VIVO de las instrucciones mientras se escriben (como el «Result» del editor de expresiones de n8n): lo que vino de un
 * dato va resaltado, lo que falta marcado. El código {{= }} lo calcula el motor (QuickJS) con los mismos datos, medio segundo después
 * de dejar de escribir; si el motor no responde se muestra lo demás y ese hueco marcado.
 */
export function LivePreview({ text, scope, hasPreTools, ran }) {
  const local = previewParts(text, scope, { keep: isPromptExpr });
  const needsEngine = local.some((p) => p.code);
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    if (!needsEngine) { setRemote(null); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      engineService.renderTemplate(text, scope, { signal: ctrl.signal, prompt: true })
        .then((r) => setRemote({ parts: r.parts, for: text }))
        .catch((e) => { if (e.code !== 'aborted') setRemote({ error: e.message, for: text }); });
    }, 500);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [needsEngine, text, scope]);
  const parts = needsEngine && remote?.parts ? remote.parts : local;
  const stale = needsEngine && remote?.for !== text;
  const missing = parts.filter((p) => p.missing || p.error).length;
  return (
    <div className="h-full flex flex-col rounded-xl bg-panel ring-1 ring-line" aria-label="Vista previa de las instrucciones">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3 pb-2 border-b border-line">
        <p className="text-[13px] font-semibold text-fg">Así las recibe el agente</p>
        <p className="text-[12px] text-fg-faint">{stale ? 'Calculando el código…' : missing ? `${missing} ${missing === 1 ? 'dato falta o falló' : 'datos faltan o fallaron'}` : 'Con el contacto de prueba'}</p>
      </div>
      {remote?.error && <p role="note" className="mx-4 mt-2 text-[12px] text-caution">El código no se pudo calcular aquí: {remote.error}</p>}
      {hasPreTools && !ran && <p className="mx-4 mt-2 text-[12px] text-fg-dim">Los datos previos aparecen después de «Probar con estos datos», abajo.</p>}
      <div className="flex-1 overflow-auto px-4 py-3 text-[13.5px] leading-[1.7] text-fg whitespace-pre-wrap break-words">
        {!text.trim() ? <span className="text-fg-faint">Escribe las instrucciones para verlas aquí ya armadas.</span>
          : parts.map((p, i) => (p.from === undefined ? <span key={i}>{p.text}</span>
            : p.error ? <mark key={i} title={`${p.from}: ${p.error}`} className="rounded bg-signal-soft px-0.5 text-signal">{p.from}</mark>
            : p.code && !remote?.parts ? <mark key={i} title="El código se calcula con el motor" className="rounded bg-caution-soft px-0.5 text-caution">{p.from}</mark>
            : p.missing ? <mark key={i} title={`${p.from}: no hay ese dato en la prueba`} className="rounded bg-caution-soft px-0.5 text-caution">{p.text || p.from}</mark>
            : <mark key={i} title={`Viene de ${p.from}`} className="rounded-md bg-live-soft px-1 font-semibold text-live ring-1 ring-inset ring-live/50 [box-decoration-break:clone]">{p.text || '∅'}</mark>))}
      </div>
    </div>
  );
}

/**
 * Contacto de prueba de la vista previa y «Probar con estos datos»: corre los datos previos DE VERDAD (consultan la base o la API) y
 * devuelve sus resultados, que la vista en vivo y el autocompletado usan desde ese momento.
 */
export function PromptTestData({ agent, draft, text, sample, onSample, run, onRun, disabled }) {
  const [state, setState] = useState({ status: 'idle' });
  const preTools = Array.isArray(draft?.config?.pre_tools) ? draft.config.pre_tools : [];
  const go = async () => {
    setState({ status: 'loading' });
    try {
      const r = await engineService.promptPreview(agent.id, { prompt: text, config: draft?.config || {}, contacto: { telefono: sample.telefono || null, nombre: sample.nombre, canal: sample.canal }, mensaje: sample.mensaje });
      onRun(r); setState({ status: 'ready' });
    } catch (e) { setState({ status: 'error', error: e.message }); }
  };
  const set = (k) => (e) => onSample({ ...sample, [k]: e.target.value });
  return (
    <section aria-labelledby="testdata-title" className="mt-6 rounded-2xl bg-panel ring-1 ring-line p-4 sm:p-5">
      <h3 id="testdata-title" className="text-[14.5px] font-semibold text-fg">Contacto de prueba</h3>
      <p className="mt-1 mb-4 text-[12.5px] text-fg-dim max-w-[72ch]">La vista previa usa estos datos. {preTools.length ? '«Probar con estos datos» también corre los datos previos (consultan de verdad tu base o API) para ver su resultado.' : 'Si agregas datos previos en Herramientas, aquí los puedes probar.'}</p>
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="Teléfono"><TextInput value={sample.telefono} onChange={set('telefono')} inputMode="tel" /></Field>
        <Field label="Nombre"><TextInput value={sample.nombre} onChange={set('nombre')} /></Field>
        <Field label="Canal">
          <NativeSelect value={sample.canal} onChange={set('canal')}><option value="whatsapp">WhatsApp</option><option value="telegram">Telegram</option><option value="web">Chat web</option></NativeSelect>
        </Field>
        <Field label="Mensaje"><TextInput value={sample.mensaje} onChange={set('mensaje')} /></Field>
      </div>
      {preTools.length > 0 && <div className="mt-3"><Btn icon={FaPlay} onClick={go} loading={state.status === 'loading'} disabled={disabled}>Probar con estos datos</Btn></div>}
      {state.status === 'error' && <p role="alert" className="mt-3 text-[13px] text-signal">{state.error}</p>}
      {run && (
        <div className="mt-4 space-y-2" aria-live="polite">
          {run.pasos?.map((p) => (
            <div key={p.as} className="rounded-lg bg-raised/60 px-3 py-2 text-[12.5px]">
              <span className={p.ok ? 'text-live' : 'text-signal'}>{p.ok ? 'Listo' : 'Falló'}</span> <code className="text-fg">datos.{p.as}</code> <span className="text-fg-faint">{p.ms} ms</span>
              {p.error ? <span className="block text-signal">{p.error}</span> : <span className="block truncate text-fg-dim" title={p.texto}>{p.texto || '(sin texto)'}</span>}
            </div>
          ))}
          {run.skipped?.length > 0 && <p className="text-[12.5px] text-caution">No corrieron: {run.skipped.join(', ')}</p>}
          {run.errors?.length > 0 && <ul className="text-[12.5px] text-signal list-disc pl-4">{run.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
        </div>
      )}
    </section>
  );
}
